import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import { MdOutlineFileCopy } from "react-icons/md";

import { getEventById } from "../../api/events";
import { getSalesOverview, getLatestTicketSales } from "../../api/tickets.js";
import { COLORS } from "../../utils/colors";
import { AllTickets } from "./AllTickets";
import { Customers } from "./Customers";
import { Overview } from "./Overview";
import FromQuestions from "./FromQuestions.jsx";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButtonGroup-grouped": {
    margin: theme.spacing(0.5),
    border: 0,
    textTransform: "none",
    width: "fit-content",
    "&.Mui-disabled": {
      border: "1px solid ${COLORS.grey}",
    },
    "&:not(:first-of-type)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-of-type": {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

export const EventStatistics = ({ userData, handleSnackbar }) => {
  const url = new URL(window.location.href);
  const id = url.searchParams.get("id");
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState({});
  const [tab, setTab] = useState("overview");
  const [dayPeriod, setDayPeriod] = useState(0);
  const [numberOfTickets, setNumberOfTickets] = useState(0);
  const [latestTickets, setlatestTickets] = useState([]);
  const [ticketSold, setTicketSold] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [invalidTickets, setInvalidTickets] = useState({
    canceled: 0,
    refunded: 0,
    invalid: 0,
  });

  useEffect(async () => {
    try {
      setLoading(true);

      const response = await getEventById(id, userData.operators[0].api_key);

      if (response.error) {
        console.log(response.error);
        setLoading(false);
        handleSnackbar(response.error.message, "error");
        return;
      }

      setEvent(response.data[0]);
      fetchSalesOverview(response.data[0]._id);

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(true);
    }
  }, []);

  useEffect(async () => {
    setLoading(true);

    try {
      setInvalidTickets({
        canceled: 0,
        refunded: 0,
        invalid: 0,
      });

      if (userData) {
        if (event._id) {
          fetchSalesOverview();
        }
      }

      setLoading(false);
    } catch (error) {
      handleSnackbar("Error while fetching data [103]", "error");
      console.log(error);
      setLoading(false);
    }
  }, [dayPeriod]);

  async function fetchSalesOverview(eventID) {
    const { data, error, status } = await getSalesOverview({
      dayPeriod,
      apiKey: userData.operators[0].api_key,
      eventID: eventID || event._id,
    });

    if (error) {
      if (!error.message.includes("No ticket found")) {
        handleSnackbar(error.message, "error");
      }
    }

    if (data && status === 200) {
      data.sort((a, b) => a._id.localeCompare(b._id));
      setNumberOfTickets(data.reduce((a, b) => a + b.tickets_sold, 0));

      const fullRangeData = createArrayOfObjects(data, dayPeriod);
      setTicketSold(fullRangeData);
    }

    const latestTicketSales = await getLatestTicketSales({
      apiKey: userData.operators[0].api_key,
      eventID: eventID || event._id,
      amount: 7,
    });

    if (latestTicketSales.error) {
      if (!latestTicketSales.error.message.includes("No ticket found")) {
        handleSnackbar(latestTicketSales.error.message, "error");
      }
    }

    if (latestTicketSales.data) {
      setlatestTickets(latestTicketSales.data);
    }

    let revenue = 0,
      invalid = 0,
      refunded = 0,
      canceled = 0;

    data?.map((day) => {
      revenue += day.revenue;
      invalid += day.invalid_tickets;
      refunded += day.refunded_tickets;
      canceled += day.canceled_tickets;
    });

    setInvalidTickets({
      canceled,
      refunded,
      invalid,
    });
    setTotalRevenue(revenue);
  }

  function createArrayOfObjects(originalArray, newDayPeriod) {
    const result = [];
    const currentDate = new Date();

    if (newDayPeriod == 0) {
      return originalArray;
    } else {
      for (let i = newDayPeriod - 1; i >= 0; i--) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - i);
        const formattedDate = date.toISOString().split("T")[0];

        const originalObject = originalArray.find(
          (obj) => obj._id === formattedDate
        );
        const tickets_sold = originalObject
          ? originalObject.tickets_sold
          : null;

        result.push({ _id: formattedDate, tickets_sold });
      }

      return result;
    }
  }

  const changeTab = (event, newTab) => {
    newTab !== null && setTab(newTab);
  };

  const changeDayPeriod = async (event, newDayPeriod) => {
    if (newDayPeriod !== null) {
      setDayPeriod(newDayPeriod);
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  return (
    <div className="insights-container">
      <p id="subheader" style={{ marginBottom: "10px" }}>
        Live Statistics
      </p>
      <h1 style={{ marginBottom: "0px" }}>{event?.name}</h1>
      <Chip
        sx={{
          width: "fit-content",
          background: COLORS.evenLighterGrey,
          border: `1px solid ${COLORS.lightGrey}`,
          margin: "5px 0 20px",
        }}
        label={`ID: ${event?._id?.slice(0, 3)}...${event?._id?.slice(21)}`}
        icon={<MdOutlineFileCopy size={16} />}
        onClick={() => copyToClipboard(event._id)}
      />
      <div
        className="container-row"
        style={{
          width: "-webkit-fill-available",
          justifyContent: "space-between",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            width: "fit-content",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <StyledToggleButtonGroup
            size="small"
            value={tab}
            exclusive
            onChange={changeTab}
          >
            <ToggleButton
              value="overview"
              selected={tab == "overview"}
              sx={{ color: COLORS.grey }}
            >
              <p
                style={{
                  fontSize: "14px",
                  margin: "0px 20px",
                }}
              >
                Overview
              </p>
            </ToggleButton>
            <ToggleButton
              value="tickets"
              selected={tab == "tickets"}
              sx={{ color: COLORS.grey }}
            >
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>
                Tickets sold
              </p>
            </ToggleButton>
            <ToggleButton
              value="customers"
              selected={tab == "customers"}
              sx={{ color: COLORS.grey }}
            >
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>Customers</p>
            </ToggleButton>
            <ToggleButton
              value="form-questions"
              selected={tab == "form-questions"}
              sx={{ color: COLORS.grey }}
            >
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>
                Form Questions
              </p>
            </ToggleButton>
          </StyledToggleButtonGroup>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            flexWrap: "wrap",
          }}
        >
          <StyledToggleButtonGroup
            size="small"
            value={dayPeriod}
            exclusive
            onChange={changeDayPeriod}
          >
            <ToggleButton value="1" selected={dayPeriod == 1}>
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>1D</p>
            </ToggleButton>
            <ToggleButton value="7" selected={dayPeriod == 7}>
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>7D</p>
            </ToggleButton>
            <ToggleButton value="15" selected={dayPeriod == 15}>
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>15D</p>
            </ToggleButton>
            <ToggleButton value="30" selected={dayPeriod == 30}>
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>30D</p>
            </ToggleButton>
            <ToggleButton value="0" selected={dayPeriod == 0}>
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>Total</p>
            </ToggleButton>
          </StyledToggleButtonGroup>
        </Paper>
      </div>
      <div className="page-content">
        {tab === "overview" && (
          <Overview
            userData={userData}
            event={event}
            loading={loading}
            handleSnackbar={handleSnackbar}
            dayPeriod={dayPeriod}
            numberOfTickets={numberOfTickets}
            latestTickets={latestTickets}
            ticketSold={ticketSold}
            totalRevenue={totalRevenue}
            invalidTickets={invalidTickets}
          />
        )}
        {tab === "customers" && (
          <Customers
            numberOfTickets={numberOfTickets}
            eventID={event._id}
            eventName={event?.name}
            loading={loading}
            event={event}
            userData={userData}
            handleSnackbar={handleSnackbar}
          />
        )}
        {tab === "tickets" && (
          <AllTickets
            event={event}
            dayPeriod={dayPeriod}
            userData={userData}
            handleSnackbar={handleSnackbar}
            numberOfTickets={numberOfTickets}
            setNumberOfTickets={setNumberOfTickets}
          />
        )}
        {tab === "form-questions" && (
          <FromQuestions
            event={event}
            dayPeriod={dayPeriod}
            userData={userData}
            handleSnackbar={handleSnackbar}
            numberOfTickets={numberOfTickets}
          />
        )}
      </div>
    </div>
  );
};
